/**
 * Firebase API Layer
 *
 * This module re-exports Firebase instances for use by API service files.
 * All services in src/api/firestore/services/ should import from this file.
 *
 * Usage:
 *   import { db } from '../firebase';
 *   import { db, storage } from '../firebase';
 *
 * @module api/firestore/firebase
 */

/* global globalThis */

import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import "firebase/compat/messaging";

const isTest = process.env.NODE_ENV === "test" || !!process.env.JEST_WORKER_ID;
const jestRef = typeof globalThis !== "undefined" ? globalThis.jest : undefined;

const createChainableDbMock = () => {
	if (!jestRef?.fn) {
		throw new Error("[api/firestore/firebase] Jest globals not available in test mode");
	}

	const db = {};
	const chain = () => db;

	db.collection = jestRef.fn(chain);
	db.doc = jestRef.fn(chain);
	db.where = jestRef.fn(chain);
	db.orderBy = jestRef.fn(chain);
	db.limit = jestRef.fn(chain);
	db.startAfter = jestRef.fn(chain);
	db.endAt = jestRef.fn(chain);
	db.onSnapshot = jestRef.fn();

	db.get = jestRef.fn();
	db.set = jestRef.fn();
	db.add = jestRef.fn();
	db.update = jestRef.fn();
	db.delete = jestRef.fn();

	db.batch = jestRef.fn(() => ({
		set: jestRef.fn(),
		update: jestRef.fn(),
		delete: jestRef.fn(),
		commit: jestRef.fn(),
	}));

	db.runTransaction = jestRef.fn(async (updateFunction) => {
		const transaction = {
			get: jestRef.fn(),
			set: jestRef.fn(),
			update: jestRef.fn(),
			delete: jestRef.fn(),
		};
		return updateFunction(transaction);
	});

	return db;
};

const createStorageMock = () => {
	if (!jestRef?.fn) {
		throw new Error("[api/firestore/firebase] Jest globals not available in test mode");
	}

	const storage = {};
	const chain = () => storage;
	storage.ref = jestRef.fn(chain);
	storage.child = jestRef.fn(chain);
	storage.put = jestRef.fn();
	storage.getDownloadURL = jestRef.fn();
	return storage;
};

let app;
let db;
let storage;

if (isTest) {
	db = createChainableDbMock();
	storage = createStorageMock();
	app = {};

	// Add minimal compat helpers used across services.
	firebase.firestore = {
		FieldValue: {
			serverTimestamp: jestRef.fn(() => ({ __type: "serverTimestamp" })),
			increment: jestRef.fn((n) => ({ __type: "increment", n })),
			arrayUnion: jestRef.fn((...items) => ({ __type: "arrayUnion", items })),
			delete: jestRef.fn(() => ({ __type: "deleteField" })),
		},
		Timestamp: {
			fromDate: jestRef.fn((date) => ({ __type: "timestamp", date })),
		},
	};
} else {
	// Reuse the existing firebase app instance from the main configuration
	// eslint-disable-next-line global-require
	app = require("../../firebase").default;
	// eslint-disable-next-line global-require
	db = require("../../firebase").db;
	storage = app.storage();
}

export { firebase, db, storage, app };
